import React, { JSX, useMemo } from "react";

import { useTheme } from "@mui/material";
import dayjs from "dayjs";
import { type EChartsOption } from "echarts-for-react";
import { useTranslation } from "react-i18next";
import { type IntervalTab } from "src/components/IntervalTabs/enum/IntervalTab";
import { chartTooltipFormatter } from "src/components/Tooltip/helpers/chartTooltipFormatter";
import type SeriesType from "src/constants/seriesType.enum";
import { getIntervalFromSelectedTab } from "src/lib/date.helper";
import { MeasuresWithHasData } from "src/types/dashboardDatasetDto";
import { MeasuresItemizedItemDto, type MeasuresItemizedDto } from "src/types/locationChartDto";

import { axisDateFormatter } from "../helpers/dashboardChart.helper";
import { ChartTooltipFormatterParams } from "../interfaces/DashboardComponentParams";

const ReactECharts = React.lazy(() => import("echarts-for-react"));

function FootfallEvolutionChart({
  measuresData,
  selectedTab,
  chartType,
  expectedVisitors,
}: {
  measuresData: MeasuresItemizedDto | null | undefined;
  selectedTab: IntervalTab;
  chartType: SeriesType;
  expectedVisitors: number | null;
}): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const subLocationsData = useMemo(() => measuresData?.data ?? [], [measuresData]) as MeasuresWithHasData<MeasuresItemizedItemDto>[];

  const option: Partial<EChartsOption> = useMemo(
    () => ({
      legend: {
        show: false,
      },
      title: {
        show: false,
      },
      tooltip: {
        trigger: "axis",
        confine: true,
        formatter: (params: ChartTooltipFormatterParams) => chartTooltipFormatter(params, t, theme, expectedVisitors, false, selectedTab),
        axisPointer: {
          type: "shadow",
        },
        borderColor: theme.palette.border.default,
      },
      grid: {
        top: 30,
        left: 3,
        right: 0,
        bottom: 0,
        containLabel: true,
      },
      calculable: true,
      xAxis: [
        {
          type: "category",
          boundaryGap: true,
          axisLabel: {
            formatter: (value: string) => {
              const intervalType = getIntervalFromSelectedTab(selectedTab);
              return axisDateFormatter(intervalType, dayjs(value), t);
            },
          },
        },
      ],
      yAxis: [
        {
          type: "value",
        },
      ],
      series: [
        {
          name: t("Footfall"),
          type: chartType,
          z: 4, // DR-2764 current footfall must overlay past data
          barGap: "0%",
          emphasis: {
            focus: "series",
          },
          data: subLocationsData.map((e) => [e.currentDate, e.currentValue, e.pastDate, e.pastValue, e.hasData]),
          itemStyle: {
            color: theme.palette.primary.main,
          },
        },
        {
          name: "Past Footfall",
          type: chartType,
          barGap: "0%",
          z: 3,
          emphasis: {
            focus: "series",
          },
          data: subLocationsData.map((e) => [e.currentDate, e.pastValue]),
          itemStyle: {
            color: theme.palette.map.secondary,
          },
        },
      ],
    }),
    [subLocationsData, selectedTab, expectedVisitors, chartType, theme, t]
  );

  return <ReactECharts option={option} />;
}

export default FootfallEvolutionChart;
